import { memo } from 'react';
import type { PlaylistItem } from '@/shared/types';
import { calcularEstadoItem } from '../../domain/usecases/validarPedido';
import type { FilaJukebox } from '../../domain/entities/FilaJukebox';
import { SongCard } from './SongCard';

interface Props {
  items:            PlaylistItem[];
  filaAtiva:        FilaJukebox[];
  cooldowns:        ReadonlyMap<string, number>;
  tocandoVideoId:   string | null;
  meuSessionId:     string;
  agora:            number;
  pedindoId:        string | null;
  onPedir:          (item: PlaylistItem) => void;
  batalhaVibesAtiva: boolean;
  meuTime:          'romance' | 'sofrencia' | null;
  tema:             { songBtnBg: string; songBtnIcon: string };
  visiveis:         number;
  sentinelRef:      (el: HTMLDivElement | null) => void;
  onAbrirBusca:     () => void;
}

function JukeboxRepertorioImpl({
  items, filaAtiva, cooldowns, tocandoVideoId,
  meuSessionId, agora, pedindoId, onPedir, batalhaVibesAtiva, meuTime, tema,
  visiveis, sentinelRef, onAbrirBusca,
}: Props) {
  // Lista já vem ordenada por pedidos_count desc — o topo é o máximo.
  // Ties no topo também levam a badge.
  const maisPedidaCount = items[0]?.pedidos_count ?? 0;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] font-medium text-white/50">
          Repertório
        </h2>
        <span className="font-mono text-xs tabular-nums text-white/50">{items.length} músicas</span>
      </div>

      <button
        type="button"
        onClick={onAbrirBusca}
        className="
          relative mb-3 flex w-full items-center gap-2.5 rounded-2xl border border-white/10
          bg-black/30 px-3.5 py-3 text-left text-sm text-white/50
          transition-colors hover:border-white/20
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60
        "
      >
        <svg className="h-4 w-4 shrink-0 text-white/25" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
        </svg>
        Buscar música ou artista...
      </button>

      <div className="space-y-2">
        {items.slice(0, visiveis).map((item, idx) => {
          const { estado, tempoRestante } = calcularEstadoItem(
            item.video_id, filaAtiva, cooldowns, tocandoVideoId, meuSessionId, agora,
          );
          return (
            <div
              key={item.id}
              className="animate-fade-in-up"
              style={{ animationDelay: `${Math.min(idx, 10) * 30}ms`, animationFillMode: 'both' }}
            >
              <SongCard
                item={item}
                estado={estado}
                tempoRestante={tempoRestante}
                onPedir={onPedir}
                pedindo={pedindoId === item.id}
                battleTeam={batalhaVibesAtiva && meuTime ? meuTime : undefined}
                songBtnBg={tema.songBtnBg}
                songBtnIcon={tema.songBtnIcon}
                maisPedida={maisPedidaCount > 0 && (item.pedidos_count ?? 0) === maisPedidaCount}
              />
            </div>
          );
        })}
        <div ref={sentinelRef} className="py-1">
          {visiveis < items.length && (
            <div className="flex items-center justify-center gap-2 py-3">
              <div className="w-4 h-4 border border-white/[0.08] border-t-orange-500 rounded-full animate-spin" />
              <span className="text-white/55 text-xs">
                Carregando mais... ({Math.min(visiveis, items.length)}/{items.length})
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const JukeboxRepertorio = memo(JukeboxRepertorioImpl);
